import { useEffect, useState } from 'react'
import axios from 'axios'
import {
  Container,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material'
import { useAuthContext } from '../../hooks/useAuthContext'
import moment from 'moment'
import { formatUserDisplayName } from '../../utility/userName'
import { useBadges } from '../../context/BadgeContext'
import { NUMBER_OF_TASKS_AND_QUERIES_ANSWERED_AT_A_TIME_BY_USER } from '../../utility/constants'

export default function MyQueries() {
  const { user } = useAuthContext()
  const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL
  const { decrementQueryBadge } = useBadges()

  let axiosOptions = {}

  if (user) {
    axiosOptions = {
      headers: { Authorization: `Bearer ${user.token}` },
    }
  }

  //state variables
  const [allUsers, setAllUsers] = useState([])
  const [queries, setQueries] = useState([])
  const [textValues, setTextValues] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(false)

  //get queries every 'x' seconds - if DB is updated, it will automatically refresh
  useEffect(() => {
    if (!initialLoading) {
      setIsLoading(true)
    }
    const interval = setInterval(async () => {
      if (user && user.id) {
        const queryData = (
          await axios.get(`${NODE_BASE_URL}/data-entry/query/user/${user.id}`, axiosOptions)
        ).data

        setQueries(queryData.queries)
        setAllUsers(queryData.users)
        setInitialLoading(true)
        setIsLoading(false)
      }
    }, 2000)

    return () => {
      clearInterval(interval)
    }
  }, [user])

  async function handleSaveAnswer(e) {
    const query_id = e.target?.name?.slice(1)
    const answer = textValues['q' + query_id] ? textValues['q' + query_id] : ''

    if (!answer || !query_id) {
      return
    }

    try {
      await axios.post(
        `${NODE_BASE_URL}/data-entry/query/answer-query`,
        { query_id, answer },
        axiosOptions,
      )

      decrementQueryBadge(NUMBER_OF_TASKS_AND_QUERIES_ANSWERED_AT_A_TIME_BY_USER)

      //refresh query data
      const queryData = (
        await axios.get(`${NODE_BASE_URL}/data-entry/query/user/${user.id}`, axiosOptions)
      ).data

      setQueries(queryData.queries)
      setAllUsers(queryData.users)
      setTextValues((prev) => {
        delete prev['q' + query_id]
        return prev
      })
    } catch (error) {
      return
    }
  }

  return (
    <Container style={{ paddingTop: '4px', paddingLeft: '4px', paddingRight: '4px' }}>
      {isLoading ? (
        <div
          style={{
            height: '80vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress color="primary" />
        </div>
      ) : queries.length > 0 ? (
        <TableContainer
          sx={{
            width: '100%',
            overflowX: 'auto',
            maxHeight: '80vh',
          }}
          component={Paper}
        >
          <Table stickyHeader aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell align="left" sx={{ width: '7%' }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    Query #
                  </Typography>
                </TableCell>
                <TableCell align="left" sx={{ width: '23%' }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    Process/Task Details
                  </Typography>
                </TableCell>
                <TableCell align="left" sx={{ width: '35%' }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    Q
                  </Typography>
                </TableCell>
                <TableCell align="left" sx={{ width: '35%' }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    A
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queries &&
                queries.map((q) => {
                  const qUser = allUsers.find((u) => u?.id == q?.question_by)
                  const aUser = allUsers.find((u) => u?.id == q?.answer_by)

                  return (
                    <TableRow key={q.id}>
                      <TableCell style={{ padding: '8px', verticalAlign: 'top' }}>
                        <Typography variant="body2">{'Q' + q.id}</Typography>
                      </TableCell>
                      <TableCell style={{ padding: '8px', verticalAlign: 'top' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {q.process_template_name}
                        </Typography>
                        <Typography variant="caption">{q.activity_template_name}</Typography>
                        <Typography variant="caption" display="block">
                          {q.prefix + '-' + q.process_reference_num}
                        </Typography>
                      </TableCell>
                      <TableCell style={{ padding: '8px', verticalAlign: 'top' }}>
                        <Typography variant="body2">{q.question}</Typography>
                        <br />
                        <Typography sx={{ fontStyle: 'italic' }} variant="caption">
                          {formatUserDisplayName(qUser)}
                        </Typography>
                        <Typography sx={{ fontStyle: 'italic' }} variant="caption" display="block">
                          {' on ' + moment(new Date(q?.question_at)).format('DD MMM YYYY, hh:mm A')}
                        </Typography>
                      </TableCell>
                      {q.is_open && q.answer_by == user.id ? (
                        <TableCell style={{ padding: '8px', verticalAlign: 'top' }}>
                          <TextField
                            variant="outlined"
                            color="primary"
                            value={textValues['q' + q.id]}
                            size="small"
                            rows={3}
                            multiline={true}
                            style={{ marginBottom: '.4rem' }}
                            onChange={(e) => {
                              setTextValues((prev) => ({
                                ...prev,
                                ['q' + q.id]: e.target.value,
                              }))
                            }}
                            fullWidth
                          />
                          <Button
                            name={'b' + q.id}
                            id={'b' + q.id}
                            color="primary"
                            variant="outlined"
                            size="small"
                            onClick={(e) => handleSaveAnswer(e)}
                          >
                            Answer Query
                          </Button>
                        </TableCell>
                      ) : (
                        <TableCell style={{ padding: '8px', verticalAlign: 'top' }}>
                          <Typography variant="body2">
                            {q.answer ? q.answer : 'Not answered yet.'}
                          </Typography>
                          <br />
                          <Typography sx={{ fontStyle: 'italic' }} variant="caption">
                            {formatUserDisplayName(aUser)}
                          </Typography>
                          <Typography
                            sx={{ fontStyle: 'italic' }}
                            variant="caption"
                            display="block"
                          >
                            {' on ' + moment(new Date(q?.answer_at)).format('DD MMM YYYY, hh:mm A')}
                          </Typography>
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body1">No Queries</Typography>
      )}
    </Container>
  )
}
